// Copyright (c) 2022, 37.78 Tecnologia Ltda. All rights reserved.

import React, { useEffect, useCallback } from "react";

import mixpanel from "mixpanel-browser";

import errorHandler from "../../util/errorHandler";
import { useAuth } from "../auth";
import { MixpanelContext } from "./MixpanelContext";

const MixpanelProvider = ({ children }) => {
  const { userState } = useAuth();
  const isPrd = process.env.REACT_APP_ENV === "production";

  const identify = useCallback(() => {
    if (userState.userId) {
      mixpanel.identify(userState.userId);
    }
  }, [userState.userId]);

  const setPeople = useCallback(() => {
    if (userState.patientData) {
      mixpanel.people.set({
        name: userState?.patientData?.fullName,
        email: userState?.patientData?.email,
        company: userState?.patientData?.organization,
      });
    }
  }, [userState.patientData]);

  useEffect(() => {
    const initialize = async () => {
      if (isPrd) {
        await mixpanel.init(process.env.REACT_APP_MIXPANEL_KEY);
      }
    };
    initialize();
  }, [isPrd]);

  useEffect(() => {
    if (isPrd) {
      try {
        identify();
        setPeople();
      } catch (error) {
        errorHandler.report(new Error(`${error.code}: ${error.message}`));
      }
    }
  }, [isPrd, identify, setPeople]);

  const track = (name, props) => {
    if (isPrd) {
      try {
        const message = `WebApp: ${name}`;
        mixpanel.track(message, {
          ...props,
          organization: userState?.patientData?.organization,
          organizationId: userState?.patientData?.organizationId,
          userId: userState?.userId,
          userName: userState?.patientData?.fullName,
        });
      } catch (error) {
        errorHandler.report(new Error(`${error.code}: ${error.message}`));
      }
    }
  };

  return (
    <MixpanelContext.Provider value={{ track }}>
      {children}
    </MixpanelContext.Provider>
  );
};

export { MixpanelProvider };
